import React, { useState } from 'react';
import { Form, Tag } from 'antd';
import PropTypes from 'prop-types';
import { toLower } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import TableComponent from '@/components/Table';
import SelectInput from '@/components/Select';
import InputComponent from '@/components/Input';
import { API } from '@/constants/api';
import ButtonComponent from '@/components/Button';
import { ReactComponent as SearchIcon } from '@/assets/images/icons/search.svg';
import { parseDateTime } from '@/utils/formatter';
import { PAGE_SIZE, ROUTES } from '@/constants';
import { TRANSACTION_STATUS } from '@/constants/transaction';
import RangePickerComponent from '@/components/RangePicker';

import './table.scss';
import moment from 'moment';

function TransactionTable({ isFilter }) {
  const { t } = useTranslation();
  const [filterForm] = Form.useForm();
  const [filterParams, setFilterParams] = useState({});
  const [triggerRefreshTable, setTriggerRefreshTable] = useState(false);
  const navigate = useNavigate();
  const handleOnFormFilterFinish = (values) => {
    const range = values.date;
    const params = {
      ...filterParams,
      ...values,
      pagination: {
        current: 1,
        pageSize: PAGE_SIZE,
      },
    };
    delete params.date;

    if (range) {
      params.fromDate = range[0].format('YYYY-MM-DD');
      params.toDate = range[1].format('YYYY-MM-DD');
    } else {
      params.fromDate = null;
      params.toDate = null;
    }

    setFilterParams({ ...params });
    setTriggerRefreshTable(true);

    setTimeout(() => {
      setTriggerRefreshTable(false);
    }, 50);
  };

  const handleResetFilterForm = () => {
    filterForm.resetFields();
    setFilterParams({});

    setTriggerRefreshTable(true);

    setTimeout(() => {
      setTriggerRefreshTable(false);
    }, 50);
  };

  const columns = [
    {
      title: t('dashboard.transaction_table.transaction_no'),
      dataIndex: 'id',
      key: 'id',
      render: (_, { id }) => <span>{id}</span>,
    },
    {
      title: t('dashboard.transaction_table.created_by'),
      dataIndex: 'createdby',
      render: (_, { user: { email }, createdAt }) => (
        <div className="created-by-container">
          {email && <div className="created-by">{email}</div>}
          {createdAt && (
            <div className="created-at">{parseDateTime(createdAt)}</div>
          )}
        </div>
      ),
    },
    {
      title: t('dashboard.transaction_table.type'),
      dataIndex: 'transactionType',
      key: 'transactionType',
      width: '200px',
      render: (_, { transactionType }) => (
        <span className="text-capitalize">
          {t(`common.transaction_types.${transactionType}`)}
        </span>
      ),
    },
    {
      title: t('dashboard.transaction_table.detail'),
      dataIndex: 'detail',
      key: 'detail',
      width: '220px',
      render: (_, { package: { nameInEnglish } }) => (
        <span>{t(`transactions_page.packages.${nameInEnglish}`)}</span>
      ),
    },
    {
      title: t('dashboard.transaction_table.payment_method'),
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      width: '220px',
      render: (_, { paymentMethod }) => (
        <span>{t(`common.payment_methods.${paymentMethod}`)}</span>
      ),
    },
    {
      title: t('dashboard.transaction_table.approved_date'),
      dataIndex: 'confirmedAt',
      key: 'confirmedAt',
      width: '400px',
      render: (_, { confirmedAt }) => {
        if (!confirmedAt) {
          return null;
        }
        return <span>{parseDateTime(confirmedAt)}</span>;
      },
    },
    {
      title: t('dashboard.transaction_table.amount'),
      dataIndex: 'amountTotal',
      key: 'amountTotal',
      render: (_, { amountTotal }) => (
        <span>฿{parseFloat(amountTotal).toLocaleString() || 0}</span>
      ),
    },
    {
      title: t('common.transactions_table.form_filter.status.label'),
      dataIndex: 'transactionStatus',
      key: 'transactionStatus',
      render: (_, { transactionStatus }) => {
        const statusText = TRANSACTION_STATUS.find(
          (item) => item.value === transactionStatus,
        )?.label;
        if (!statusText) {
          return null;
        }
        return (
          <Tag
            key={statusText}
            className={`sms-tag sms-${toLower(transactionStatus)}`}
          >
            <span>
              {t(`common.constants.transaction_status.lower.${statusText}`)}
            </span>
          </Tag>
        );
      },
    },
  ];

  const locale = {
    emptyText: (
      <div className="transaction-table__empty">
        <span className="transaction-table__empty__icon" />
        <p className="transaction-table__empty__title">
          {t('common.transactions_table.no_transaction')}
        </p>
        <ButtonComponent onClick={() => navigate(ROUTES.SMS.INDEX)}>
          {t('common.transactions_table.btn_quick_send')}
        </ButtonComponent>
        <ButtonComponent
          type="primary"
          onClick={() => navigate(ROUTES.SUBSCRIPTIONS.INDEX)}
        >
          {t('common.transactions_table.btn_subscription')}
        </ButtonComponent>
      </div>
    ),
  };

  const [dates, setDates] = useState(null);
  const disabledDate = (current) => {
    const oneMonthAgo = moment().subtract(1, 'months');

    if (!dates) {
      return current && current < oneMonthAgo.startOf('day');
    }
    const tooLate = dates[0] && current.diff(dates[0], 'days') > 7;
    const tooEarly = dates[1] && dates[1].diff(current, 'days') > 7;

    return !!tooEarly || !!tooLate;
  };

  const onChangeDatePicker = (val) => {
    if (!val) {
      setDates(null);
    }
  };

  const transactionStatusOptions = TRANSACTION_STATUS.map((status) => ({
    label: t(`common.constants.transaction_status.lower.${status.label}`),
    value: status.value,
  }));

  const renderFormFilter = () => (
    <div className="form-filter">
      <Form
        name="filter"
        layout="vertical"
        onFinish={handleOnFormFilterFinish}
        autoComplete="off"
        className="form"
        form={filterForm}
      >
        <Form.Item
          label={t(
            'common.transactions_table.form_filter.transaction_number.label',
          )}
          name="id"
          className="form-item"
        >
          <InputComponent
            placeholder={t(
              'common.transactions_table.form_filter.transaction_number.placeholder',
            )}
          />
        </Form.Item>

        <Form.Item
          label={t('common.transactions_table.form_filter.status.label')}
          name="status"
          className="form-item"
        >
          <SelectInput
            allowClear
            placeholder={t(
              'common.transactions_table.form_filter.status.placeholder',
            )}
            options={transactionStatusOptions}
          />
        </Form.Item>
        <Form.Item
          label={t('common.transactions_table.form_filter.date.label')}
          name="date"
          className="form-item"
        >
          <RangePickerComponent
            name="date"
            disabledDate={disabledDate}
            onCalendarChange={(val) => setDates(val)}
            inputReadOnly
            placeholder={[
              t('transactions_page_trans.start_date'),
              t('transactions_page_trans.end_date'),
            ]}
            onChange={onChangeDatePicker}
          />
        </Form.Item>
        <Form.Item>
          <div className="form-actions-item">
            <ButtonComponent
              type="primary"
              htmlType="submit"
              icon={<SearchIcon />}
            >
              {t('common.btn_search')}
            </ButtonComponent>
            <ButtonComponent type="default" onClick={handleResetFilterForm}>
              {t('common.btn_reset')}
            </ButtonComponent>
          </div>
        </Form.Item>
      </Form>
    </div>
  );

  return (
    <div className="transaction-table">
      {isFilter && renderFormFilter()}
      <TableComponent
        locale={locale}
        columns={columns}
        filterParams={filterParams}
        action={API.TRANSACTION.INDEX}
        triggerRefresh={triggerRefreshTable}
        onRow={(record) => ({
          onClick: () => {
            navigate(`${ROUTES.TRANSACTIONS.INDEX}/${record.id}`);
          },
        })}
        scroll={{
          scrollToFirstRowOnChange: true,
          x: 1200,
        }}
      />
    </div>
  );
}

TransactionTable.propTypes = {
  isFilter: PropTypes.bool,
};

export default TransactionTable;
